import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/currency';
import { shareWeekend, shareWeekendSet } from '../lib/share';
import { AirportAutocomplete } from './AirportAutocomplete';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { PlaneTakeoff, PlaneLanding, CalendarDays, Loader2, Search, Sparkles, ArrowRight, MapPin, Share2, Bell, Check } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function nextMonths(n = 12) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + 1); // start next month
  for (let i = 0; i < n; i++) {
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ ym, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
    d.setMonth(d.getMonth() + 1);
  }
  return out;
}

function fmtDay(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return { day: d.getDate(), wd: WD[(d.getDay() + 6) % 7], mon: MONTHS[d.getMonth()].slice(0, 3) };
  } catch { return { day: '', wd: '', mon: '' }; }
}

const firstCode = (c) => (c || '').split(',')[0];

// A single weekend trip card: tap to search, share it, or set a price-drop alert.
const WeekendCard = ({ w, index, originCode, currency, onOpen }) => {
  const dep = fmtDay(w.depart_date);
  const ret = fmtDay(w.return_date);
  const [alertOpen, setAlertOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submitAlert = async () => {
    if (!email || !/.+@.+\..+/.test(email)) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try {
      await api.createFareAlert({
        origin: originCode, destination: w.destination, email,
        depart_date: w.depart_date, round_trip: true, trip_days: w.nights,
        target_price: target ? Number(target) : null, currency,
      });
      setDone(true);
      toast.success('Weekend alert set — we\u2019ll email you when the fare drops.');
    } catch {
      toast.error('Could not set the alert. Try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4 hover:border-accent/40 transition-colors" data-testid={`weekend-card-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {w.destination_city && (
            <p className="font-semibold truncate" data-testid={`weekend-dest-${index}`}>{w.destination_city} <span className="text-xs font-mono text-muted-foreground">{w.destination}</span></p>
          )}
          <p className="text-sm font-medium flex items-center gap-1.5 mt-0.5">
            <span className="font-semibold">{dep.wd} {dep.day} {dep.mon}</span>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-semibold">{ret.wd} {ret.day} {ret.mon}</span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{w.nights} nights · round trip</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">from</p>
          <p className="text-xl font-semibold tabular" data-testid={`weekend-price-${index}`}>{formatPrice(w.price_inr, currency)}</p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button onClick={() => onOpen(w)} className="flex-1 h-9 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 text-sm font-semibold" data-testid={`weekend-search-trip-${index}`}>
          Search this trip <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
        <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl shrink-0" title="Share this weekend" onClick={() => shareWeekend(w, originCode, currency)} data-testid={`weekend-share-${index}`}>
          <Share2 className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className={`h-9 w-9 rounded-xl shrink-0 ${alertOpen || done ? 'border-accent/50 text-accent' : ''}`} title="Alert me when this drops" onClick={() => !done && setAlertOpen((v) => !v)} data-testid={`weekend-alert-toggle-${index}`}>
          {done ? <Check className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
        </Button>
      </div>

      {done && (
        <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5" data-testid={`weekend-alert-done-${index}`}>
          <Check className="h-3.5 w-3.5" /> Alert set — we’ll email {email} on a price drop.
        </p>
      )}
      {alertOpen && !done && (
        <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid={`weekend-alert-form-${index}`}>
          <p className="text-[11px] text-muted-foreground">Get an email when this {originCode}→{w.destination} weekend drops in price.</p>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
            className="w-full h-9 rounded-lg border border-input px-2 text-sm" data-testid={`weekend-alert-email-${index}`} />
          <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={`Notify under (${formatPrice(w.price_inr, currency).replace(/[\d.,\s]/g, '')}) — optional`}
            className="w-full h-9 rounded-lg border border-input px-2 text-sm" data-testid={`weekend-alert-target-${index}`} />
          <Button onClick={submitAlert} disabled={busy} className="w-full h-9 rounded-lg bg-accent text-accent-foreground text-sm font-semibold" data-testid={`weekend-alert-submit-${index}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bell className="h-4 w-4 mr-1.5" /> Set price alert</>}
          </Button>
        </div>
      )}
    </div>
  );
};

export const WeekendFinder = ({ variant = 'section', initial, syncUrl }) => {
  const navigate = useNavigate();
  const { currency } = useApp();
  const months = useMemo(() => nextMonths(12), []);
  const [origin, setOrigin] = useState(initial?.originCode ? { code: initial.originCode, city: initial.originCode } : null);
  const [dest, setDest] = useState(initial?.destCode ? { code: initial.destCode, city: initial.destCode } : null);
  const [anywhere, setAnywhere] = useState(!!initial?.anywhere);
  const [month, setMonth] = useState(initial?.month && months.some((m) => m.ym === initial.month) ? initial.month : months[0].ym);
  const [longWeekend, setLongWeekend] = useState(!!initial?.longWeekend);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const didInit = useRef(false);

  const runSearch = useCallback(async (oCode, dCode, m, lw) => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.weekendFares({ origin: oCode, destination: dCode || '', month: m, long_weekend: lw, currency });
      setResult(data);
      if (!data.weekends.length) toast.info('No live weekend fares cached for that month yet — try another month.');
    } catch {
      toast.error('Could not load weekend fares. Try again.');
    } finally {
      setLoading(false);
    }
  }, [currency]);

  const search = () => {
    if (!origin) { toast.error('Pick where you\u2019re flying from'); return; }
    const oCode = firstCode(origin.code);
    const dCode = anywhere ? '' : (dest ? firstCode(dest.code) : '');
    if (!anywhere && !dCode) { toast.error('Pick a destination — or switch to \u201cAnywhere\u201d'); return; }
    if (syncUrl) {
      const qp = { origin: oCode, month };
      if (anywhere) qp.anywhere = '1'; else qp.destination = dCode;
      if (longWeekend) qp.long = '1';
      navigate(`/flights/weekends?${new URLSearchParams(qp).toString()}`, { replace: true });
    }
    runSearch(oCode, dCode, month, longWeekend);
  };

  // Auto-run when opened via a shared/deep link (?origin=..&destination=..|anywhere=1&month=..).
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (initial?.originCode) {
      runSearch(initial.originCode, initial.anywhere ? '' : (initial.destCode || ''),
        month, !!initial.longWeekend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTrip = (w) => {
    const q = new URLSearchParams({
      origin: firstCode(origin.code), destination: w.destination,
      trip_type: 'round_trip', depart_date: w.depart_date, return_date: w.return_date,
      adults: 1, children: 0, cabin: 'economy',
    }).toString();
    navigate(`/flights/results?${q}`);
  };

  const shareSet = () => {
    if (!result || !result.weekends.length) return;
    shareWeekendSet({
      originCode: result.origin, originCity: result.origin_city,
      destCode: result.destination, destCity: result.destination_city,
      month: result.month, monthLabel: months.find((m) => m.ym === result.month)?.label || result.month,
      longWeekend: result.long_weekend, anywhere: result.mode === 'anywhere',
      cheapestInr: result.weekends[0]?.price_inr, currency,
    });
  };

  return (
    <div data-testid="weekend-finder">
      <div className={`rounded-2xl border border-border bg-card p-4 sm:p-5 ${variant === 'page' ? '' : 'card-elevate'}`}>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-accent/12 text-accent"><Sparkles className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="font-semibold leading-tight">Weekend Finder</p>
            <p className="text-xs text-muted-foreground leading-tight">Cheapest {longWeekend ? 'long-weekend' : 'Fri\u2013Sun'} round trips, ranked by price.</p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-12 items-end">
          <div className="md:col-span-3 relative">
            <AirportAutocomplete label="From" placeholder="City or airport" value={origin} onChange={setOrigin} testid="weekend-from-input" icon={PlaneTakeoff} />
          </div>
          <div className="md:col-span-3 relative">
            {anywhere ? (
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">To</label>
                <div className="flex items-center gap-2 rounded-xl border border-dashed border-accent/40 bg-accent/[0.04] px-3 h-12 text-sm font-medium text-accent" data-testid="weekend-anywhere-field">
                  <MapPin className="h-4 w-4" /> Anywhere · surprise me
                </div>
              </div>
            ) : (
              <AirportAutocomplete label="To" placeholder="City or airport" value={dest} onChange={setDest} testid="weekend-to-input" icon={PlaneLanding} />
            )}
          </div>
          <div className="md:col-span-3">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Month</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="weekend-month-select">
                <CalendarDays className="h-4 w-4 text-accent mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => <SelectItem key={m.ym} value={m.ym} data-testid={`weekend-month-${m.ym}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Button onClick={search} disabled={loading} className="w-full h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="weekend-search-button">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Search className="h-5 w-5 mr-2" /> Find weekends</>}
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setAnywhere((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${anywhere ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`}
            data-testid="weekend-anywhere-toggle">
            <MapPin className="h-3.5 w-3.5" /> Anywhere
          </button>
          <button type="button" onClick={() => setLongWeekend((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${longWeekend ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`}
            data-testid="weekend-longweekend-toggle">
            Long weekend (3 nights)
          </button>
        </div>
      </div>

      {loading && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="weekend-loading">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-28 rounded-2xl bg-secondary/40 animate-pulse" />)}
        </div>
      )}

      {!loading && result && (
        <div className="mt-5" data-testid="weekend-results">
          {result.weekends.length === 0 ? (
            <div className="text-center py-12 rounded-2xl border border-dashed border-border" data-testid="weekend-empty">
              <p className="font-medium">No weekend fares found for {months.find((m) => m.ym === result.month)?.label}</p>
              <p className="text-sm text-muted-foreground">Live fares aren\u2019t cached for this month/route yet. Try another month.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{result.weekends.length}</span> {result.long_weekend ? 'long-weekend' : 'weekend'} {result.weekends.length === 1 ? 'trip' : 'trips'}
                  {result.mode === 'anywhere' ? ' to popular escapes' : ` to ${result.destination_city || result.destination}`}
                  <span className="ml-2 text-xs bg-accent/10 text-accent px-2 py-0.5 rounded">indicative round-trip fares</span>
                </p>
                <Button variant="outline" size="sm" className="rounded-xl shrink-0" onClick={shareSet} data-testid="weekend-share-set-button">
                  <Share2 className="h-4 w-4 mr-1.5" /> Share these
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2" data-testid="weekend-cards">
                {result.weekends.map((w, i) => (
                  <WeekendCard key={`${w.destination}-${w.depart_date}`} w={w} index={i}
                    originCode={result.origin} currency={currency} onOpen={openTrip} />
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">Fares are live indicative round-trip totals and change constantly. Tap a weekend to run a full live search before booking.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
